// src/components/MemberRenewContainer/DependentsTable.jsx
import React from 'react';
import PropTypes from 'prop-types';
import LoadingIndicator from 'components/loading-indicator';
import MemberFormModal from './MemberFormModal';
import { lowerCase } from 'lodash';

const DEFAULT_PROFILE_IMAGE = 'https://static.thenounproject.com/png/user-default-icon-4154905-512.png';

const DependentsTable = ({ dependents, setDependents, updateMember: callParent, renewalProgress, selectedSecondWifeId, setSelectedSecondWifeId }) => {
  // State for editing
  const [showEditModal, setShowEditModal] = React.useState(false);
  const [isEditing, setIsEditing] = React.useState(false);
  const [currentEditMember, setCurrentEditMember] = React.useState({
    fullName: '',
    birthdate: '',
    gender: '',
    membershipNumber: '',
    localMembershipNumber: '',
    nationalId: '',
    relationshipToHead: '',
    membershipType: '',
    profession: '',
    photo: null,
    id: null,
  });

  // Handler to open edit modal
  const handleEditClick = (member) => {
    setIsEditing(true);
    setCurrentEditMember({
      fullName: member.fullName,
      birthdate: member.birthdate,
      gender: member.gender,
      membershipNumber: member.membershipNumber || '',
      localMembershipNumber: member.localMembershipNumber,
      nationalId: member.nationalId || '',
      relationshipToHead: member.relationshipToHead,
      membershipType: member.membershipType || '',
      profession: member.profession,
      photo: null,
      id: member.id,
    });
    setShowEditModal(true);
  };

  // Handler for form field changes in Edit Modal
  const handleEditChange = (e) => {
    const { name, value, files } = e.target;
    setCurrentEditMember((prev) => ({
      ...prev,
      [name]: files ? files[0] : value,
    }));
  };

  // Handler for date changes in Edit Modal
  const handleEditDateChange = (gregorianDate, field) => {
    setCurrentEditMember((prev) => ({
      ...prev,
      [field]: gregorianDate,
    }));
  };

  // Handler to save edited member
  const saveEditedMember = async (memberData) => {
    await callParent(memberData);
    // Optionally, you can update the local state here if needed
  };

  // Handler to toggle the 'enabled' status
  const handleToggleEnabled = async (depId) => {
    const updatedDependents = dependents.map((dep) => {
      if (dep.id === depId) {
        const updatedDep = { ...dep, enabled: !dep.enabled };
        return updatedDep;
      }
      return dep;
    });
    setDependents(updatedDependents);
  };

  // Helper function to render enrollment status badge
  const renderEnrollmentStatus = (isActive) => {
    if (isActive) {
      return (
        <span className="tw-inline-flex tw-items-center tw-bg-gradient-to-r tw-from-green-100 tw-to-emerald-100 tw-text-green-800 tw-text-xs tw-font-semibold tw-px-3 tw-py-1.5 tw-rounded-full tw-border tw-border-green-300 tw-shadow-sm">
          <svg className="tw-w-3 tw-h-3 tw-mr-1.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          Active
        </span>
      );
    } else {
      return (
        <span className="tw-inline-flex tw-items-center tw-bg-gradient-to-r tw-from-red-100 tw-to-rose-100 tw-text-red-800 tw-text-xs tw-font-semibold tw-px-3 tw-py-1.5 tw-rounded-full tw-border tw-border-red-300 tw-shadow-sm">
          <svg className="tw-w-3 tw-h-3 tw-mr-1.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          Expired
        </span>
      );
    }
  };

  // Helper function to render renewal progress
  const renderRenewalProgress = (status) => {
    switch (status) {
      case 'loading':
        return <LoadingIndicator noun="Renewing" />;
      case 'success':
        return (
          <span className="tw-inline-flex tw-items-center tw-text-green-600 tw-font-semibold">
            <svg className="tw-w-4 tw-h-4 tw-mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            Renewed
          </span>
        );
      case 'error':
        return (
          <span className="tw-inline-flex tw-items-center tw-text-red-600 tw-font-semibold">
            <svg className="tw-w-4 tw-h-4 tw-mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            Error
          </span>
        );
      default:
        return null;
    }
  };

  // Function to get all wives
  const getWives = () => {
    return dependents.filter(dep => {
      const relationship = lowerCase(dep.relationshipToHead);
      return relationship === 'wife';
    });
  };

  const wives = getWives();
  const hasMultipleWives = wives.length > 1;
  // Get all wives except the first one (additional wives that need to pay the fee)
  const additionalWives = hasMultipleWives ? wives.slice(1) : [];

  const showSwitchButton = dependent => {
    const relationship = lowerCase(dependent.relationshipToHead);
    const isAdultChild = dependent.age >= 18 && (relationship === 'son' || relationship === 'daughter');
    const isMinorChild = dependent.age < 18 && (relationship === 'son' || relationship === 'daughter');
    const isSpouseOrSelf = relationship === 'husband' || relationship === 'wife' || relationship === 'self';
  
    if (dependent.active || isSpouseOrSelf || isMinorChild) {
      return false;
    }
  
    return isAdultChild || true;
  };

  // Check if this dependent is the second wife and should show the additional wife fee switch
  

  return (
    <div className="tw-overflow-x-auto tw-rounded-lg tw-border tw-border-gray-200">
      <table className="tw-w-full tw-min-w-[1200px]">
        <thead>
          <tr className="tw-bg-gradient-to-r tw-from-gray-50 tw-to-gray-100 tw-border-b-2 tw-border-gray-300">
            <th className="tw-px-5 tw-py-4 tw-text-left tw-text-sm tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider">Member Details</th>
            <th className="tw-px-5 tw-py-4 tw-text-center tw-text-sm tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider">Enrollment Status</th>
            <th className="tw-px-5 tw-py-4 tw-text-center tw-text-sm tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider">Enabled</th>
            <th className="tw-px-5 tw-py-4 tw-text-center tw-text-sm tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody className="tw-bg-white tw-divide-y tw-divide-gray-200">
          {dependents.map((dep) => {
            // Construct the full photo URL
            const photoUrl = dep.photoUrl
              ? `${process.env.REACT_APP_COVERAGE_API_BASE}${dep.photoUrl}`
              : DEFAULT_PROFILE_IMAGE;

            // Check if this dependent is an additional wife (not the first wife) and should show the additional wife fee switch
            const isAdditionalWife = additionalWives.some(wife => wife.id === dep.id);
            // Hide switch if enrollment is active (check both active and activeEnollment properties)
            const isEnrollmentActive = dep.active || dep.activeEnollment;
            const showAdditionalWifeSwitch = isAdditionalWife && hasMultipleWives && !isEnrollmentActive;

            // Handler for toggling additional wife selection
            const handleAdditionalWifeToggle = (wifeId) => {
              if (setSelectedSecondWifeId) {
                const currentSelection = Array.isArray(selectedSecondWifeId) ? selectedSecondWifeId : (selectedSecondWifeId ? [selectedSecondWifeId] : []);
                if (currentSelection.includes(wifeId)) {
                  // Remove from selection
                  setSelectedSecondWifeId(currentSelection.filter(id => id !== wifeId));
                } else {
                  // Add to selection
                  setSelectedSecondWifeId([...currentSelection, wifeId]);
                }
              }
            };

            // Check if this wife is currently selected
            const isWifeSelected = Array.isArray(selectedSecondWifeId) 
              ? selectedSecondWifeId.includes(dep.id)
              : (selectedSecondWifeId === dep.id);

            return (
              <tr key={dep.id} className="tw-transition-colors tw-duration-150 hover:tw-bg-gray-50">
                {/* Member Details Column */}
                <td className="tw-px-5 tw-py-4 tw-whitespace-nowrap">
                  <div className="tw-flex tw-items-center">
                    <div className="tw-relative">
                      <img
                        src={photoUrl}
                        alt={`${dep.fullName}'s profile`}
                        className="tw-w-16 tw-h-16 tw-rounded-full tw-object-cover tw-mr-4 tw-border-2 tw-border-gray-200 tw-shadow-sm"
                      />
                      <div className="tw-absolute tw-bottom-0 tw-right-3 tw-bg-white tw-rounded-full tw-p-1 tw-shadow-md">
                        <svg className="tw-w-3 tw-h-3 tw-text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                    <div className="tw-flex tw-flex-col tw-items-start">
                      <span className="tw-font-bold tw-text-gray-900 tw-text-base tw-mb-1">{dep.fullName}</span>
                      <div className="tw-flex tw-flex-wrap tw-gap-x-3 tw-gap-y-1">
                        <span className="tw-text-xs tw-text-gray-600 tw-bg-gray-100 tw-px-2 tw-py-0.5 tw-rounded">
                          <span className="tw-font-semibold">Sex:</span> {dep.gender === 'M' ? 'Male' : 'Female'}
                        </span>
                        <span className="tw-text-xs tw-text-gray-600 tw-bg-gray-100 tw-px-2 tw-py-0.5 tw-rounded">
                          <span className="tw-font-semibold">Age:</span> {dep.age}
                        </span>
                        <span className="tw-text-xs tw-text-gray-600 tw-bg-gray-100 tw-px-2 tw-py-0.5 tw-rounded">
                          <span className="tw-font-semibold">Membership:</span> {dep.membershipNumber || 'N/A'}
                        </span>
                        <span className="tw-text-xs tw-text-gray-600 tw-bg-gray-100 tw-px-2 tw-py-0.5 tw-rounded">
                          <span className="tw-font-semibold">Profession:</span> {dep.profession || 'N/A'}
                        </span>
                        <span className="tw-text-xs tw-text-gray-600 tw-bg-gray-100 tw-px-2 tw-py-0.5 tw-rounded">
                          <span className="tw-font-semibold">Relationship:</span> {dep.relationshipToHead}
                        </span>
                      </div>
                    </div>
                  </div>
                </td>
                {/* Enrollment Status Column */}
                <td className="tw-px-5 tw-py-4 tw-whitespace-nowrap tw-text-center">
                  <div className="tw-flex tw-justify-center tw-items-center">
                    {renderEnrollmentStatus(dep.activeEnollment)}
                  </div>
                </td>
                
                {/* Enabled Column */}
                <td className="tw-px-5 tw-py-4 tw-whitespace-nowrap tw-text-center">
                  <div className="tw-flex tw-flex-col tw-items-center tw-gap-2">
                    {showSwitchButton(dep) ? (
                      <div className="tw-flex tw-justify-center tw-items-center">
                        <label className="tw-relative tw-inline-flex tw-flex-shrink-0 tw-border-2 tw-border-transparent tw-rounded-full tw-w-11 tw-h-6 tw-cursor-pointer focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2 focus:tw-ring-indigo-500 tw-transition-all">
                          <input
                            type="checkbox"
                            checked={dep.enabled}
                            onChange={() => handleToggleEnabled(dep.id)}
                            className="tw-absolute tw-opacity-0 tw-rounded-full tw-h-6 tw-w-11"
                            aria-label={`Toggle enabled status for ${dep.fullName}`}
                          />
                          <span
                            className={`tw-absolute tw-rounded-full tw-h-6 tw-w-11 tw-transition-colors tw-duration-200 tw-shadow-inner ${
                              dep.enabled ? 'tw-bg-gradient-to-r tw-from-indigo-500 tw-to-indigo-600' : 'tw-bg-gray-300'
                            }`}
                          ></span>
                          <span
                            className={`tw-absolute tw-left-0.5 tw-top-0.5 tw-bg-white tw-rounded-full tw-h-5 tw-w-5 tw-transform tw-transition-transform tw-duration-200 tw-shadow-md ${
                              dep.enabled ? 'tw-translate-x-5' : 'tw-translate-x-0'
                            }`}
                          ></span>
                        </label>
                      </div>
                    ) : (
                      <span className="tw-text-gray-400 tw-text-xs">N/A</span>
                    )}
                    {/* Additional Wife Fee Switch */}
                    {showAdditionalWifeSwitch && (
                      <div className="tw-flex tw-flex-col tw-items-center tw-gap-1">
                        <label className="tw-relative tw-inline-flex tw-flex-shrink-0 tw-border-2 tw-border-transparent tw-rounded-full tw-w-11 tw-h-6 tw-cursor-pointer focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2 focus:tw-ring-green-500 tw-transition-all">
                          <input
                            type="checkbox"
                            checked={isWifeSelected}
                            onChange={() => handleAdditionalWifeToggle(dep.id)}
                            className="tw-absolute tw-opacity-0 tw-rounded-full tw-h-6 tw-w-11"
                            aria-label={`Toggle additional wife fee for ${dep.fullName}`}
                          />
                          <span
                            className={`tw-absolute tw-rounded-full tw-h-6 tw-w-11 tw-transition-colors tw-duration-200 tw-shadow-inner ${
                              isWifeSelected ? 'tw-bg-gradient-to-r tw-from-green-500 tw-to-green-600' : 'tw-bg-gray-300'
                            }`}
                          ></span>
                          <span
                            className={`tw-absolute tw-left-0.5 tw-top-0.5 tw-bg-white tw-rounded-full tw-h-5 tw-w-5 tw-transform tw-transition-transform tw-duration-200 tw-shadow-md ${
                              isWifeSelected ? 'tw-translate-x-5' : 'tw-translate-x-0'
                            }`}
                          ></span>
                        </label>
                        <span className="tw-text-xs tw-text-gray-600 tw-font-medium">Additional Wife Fee</span>
                      </div>
                    )}
                  </div>
                </td>
                {/* Actions */}
                <td className="tw-px-5 tw-py-4 tw-whitespace-nowrap tw-text-center">
                  <div className="tw-flex tw-justify-center tw-items-center tw-space-x-2">
                    <button
                      onClick={() => handleEditClick(dep)}
                      className="tw-bg-gradient-to-r tw-from-blue-500 tw-to-blue-600 tw-text-white tw-text-xs tw-px-3 tw-py-1.5 tw-rounded-lg hover:tw-from-blue-600 hover:tw-to-blue-700 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-400 focus:tw-ring-offset-1 tw-shadow-sm hover:tw-shadow-md tw-transition-all tw-duration-200 tw-flex tw-items-center"
                      aria-label={`Edit ${dep.fullName}`}
                    >
                      <svg className="tw-w-3 tw-h-3 tw-mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Edit
                    </button>
                    <button
                      onClick={() => window.open(`/members/${dep.id}`, '_blank')}
                      className="tw-bg-gradient-to-r tw-from-green-500 tw-to-emerald-500 tw-text-white tw-text-xs tw-px-3 tw-py-1.5 tw-rounded-lg hover:tw-from-green-600 hover:tw-to-emerald-600 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-green-400 focus:tw-ring-offset-1 tw-shadow-sm hover:tw-shadow-md tw-transition-all tw-duration-200 tw-flex tw-items-center"
                      aria-label={`View Details for ${dep.fullName}`}
                    >
                      <svg className="tw-w-3 tw-h-3 tw-mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      Details
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Edit Member Modal */}
      <MemberFormModal
        showModal={showEditModal}
        toggleModal={() => setShowEditModal(false)}
        newMember={currentEditMember}
        handleChange={handleEditChange}
        saveMember={saveEditedMember}
        isEditing={isEditing}
        hasHouseholdHead={false}
        handleDateChange={handleEditDateChange}
        errors={{}}
      />

      {/* Optional: Display mutation error */}
      {/* Since archive logic is removed, and assuming updateMember mutation was solely for archiving,
          you might want to remove this section as well if it's no longer applicable. */}
    </div>
  );
};

DependentsTable.propTypes = {
  dependents: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      photoUrl: PropTypes.string,
      fullName: PropTypes.string.isRequired,
      gender: PropTypes.string.isRequired,
      age: PropTypes.number.isRequired,
      birthdate: PropTypes.string.isRequired,
      membershipNumber: PropTypes.string,
      localMembershipNumber: PropTypes.string.isRequired,
      relationshipToHead: PropTypes.string.isRequired,
      membershipType: PropTypes.string,
      profession: PropTypes.string,
      active: PropTypes.bool.isRequired, // Existing field
      enabled: PropTypes.bool, // Added 'enabled' field
      // Removed archive-related props
    })
  ).isRequired,
  setDependents: PropTypes.func.isRequired, // Ensure setDependents is required
  updateMember: PropTypes.func.isRequired, // Assuming this is still needed for saving edits
  renewalProgress: PropTypes.object, // New prop to track renewal progress
  selectedSecondWifeId: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]), // ID(s) of selected additional wives for additional fee
  setSelectedSecondWifeId: PropTypes.func, // Function to set selected additional wife ID(s)
};

DependentsTable.defaultProps = {
  renewalProgress: {}, // Default to empty object if not provided
  // enabled is handled per dependent; no default needed here
};

export default DependentsTable;
